import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import { makeStyles } from "@material-ui/core/styles";
import { authGet } from "../../lib";

/**
 * Repair (RMA) requests — a port of the legacy rma.php list.
 *
 * A repair has no status column: rma_no carries it, so the row tint is derived
 * the same way the legacy template derived its row class.
 */
const STATUS_STYLE = {
  Pending: { rgb: "13,71,161" },
  Approved: { rgb: "27,94,32" },
  Denied: { rgb: "183,28,28" },
  Cancelled: { rgb: "141,110,79" },
};

const statusRowStyles = Object.entries(STATUS_STYLE).reduce((acc, [name, s]) => {
  acc[`status${name}`] = {
    backgroundColor: `rgba(${s.rgb}, 0.18)`,
    "&:hover": { backgroundColor: `rgba(${s.rgb}, 0.28)` },
  };
  return acc;
}, {});

const useStyles = makeStyles((theme) => ({
  page: { maxWidth: 1360, margin: "24px auto 0", width: "100%" },
  panel: { padding: theme.spacing(2.5) },
  head: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  title: { fontSize: 18, fontWeight: 500, flex: "none" },
  spacer: { flex: 1 },
  search: {
    width: 320,
    "& .MuiOutlinedInput-root": { height: 31 },
    "& .MuiOutlinedInput-input": { padding: "0 4px", fontSize: 13 },
    "& .MuiInputAdornment-root": { marginRight: 2 },
  },
  searchIcon: { fontSize: 17, color: theme.palette.text.secondary },
  count: { color: theme.palette.text.secondary, fontSize: 13 },
  row: { cursor: "pointer", height: 36 },
  cell: { fontSize: 13, padding: "4px 10px" },
  headCell: { fontSize: 12, fontWeight: 600, padding: "6px 10px" },
  strong: { color: "rgba(0,0,0,0.87)" },
  muted: { color: theme.palette.text.secondary },
  truncate: { maxWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  loading: { padding: theme.spacing(4), textAlign: "center" },
  error: { color: "#c62828", fontSize: 13 },
  ...statusRowStyles,
}));

const when = (s) => {
  if (!s) return "";
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(s) : d.toLocaleDateString();
};

export function RepairsPage() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const urlSearch = params.get("search") ?? "";

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState(urlSearch);

  useEffect(() => setSearch(urlSearch), [urlSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const query = urlSearch ? `?search=${encodeURIComponent(urlSearch)}` : "";
    const { data, error: err } = await authGet(`/repairs${query}`);
    setLoading(false);

    if (err) {
      setError(err.message || "Could not load repairs.");
      return;
    }
    setRows(data?.rows ?? []);
    setTotal(data?.total ?? 0);
  }, [urlSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = (e) => {
    e?.preventDefault?.();
    const next = search.trim();
    history.push(next ? `/repairs?search=${encodeURIComponent(next)}` : "/repairs");
  };

  return (
    <Box className={classes.page}>
      <Paper className={classes.panel}>
        <div className={classes.head}>
          <Typography className={classes.title}>Repairs</Typography>

          <form onSubmit={submit}>
            <TextField
              className={classes.search}
              placeholder="Email, name, phone, RMA #, brand or model"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onBlur={submit}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className={classes.searchIcon} />
                  </InputAdornment>
                ),
              }}
            />
          </form>

          <div className={classes.spacer} />
          <span className={classes.count}>
            {loading ? "Loading…" : `${total.toLocaleString()} shown`}
          </span>
        </div>

        {error && <div className={classes.error}>{error}</div>}

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.headCell} style={{ width: 90 }}>Request</TableCell>
              <TableCell className={classes.headCell} style={{ width: 90 }}>Date</TableCell>
              <TableCell className={classes.headCell}>Name</TableCell>
              <TableCell className={classes.headCell}>Email</TableCell>
              <TableCell className={classes.headCell} style={{ width: 120 }}>Phone</TableCell>
              <TableCell className={classes.headCell}>Watch</TableCell>
              <TableCell className={classes.headCell} style={{ width: 110 }}>RMA #</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow
                key={r.rma_id}
                className={`${classes.row} ${classes[`status${r.status}`] ?? ""}`}
                onClick={() => history.push(`/repairs/${r.rma_id}`)}
              >
                <TableCell className={`${classes.cell} ${classes.strong}`}>{r.rma_id}</TableCell>
                <TableCell className={classes.cell}>{when(r.created)}</TableCell>
                <TableCell className={`${classes.cell} ${classes.strong}`}>
                  {[r.first_name, r.last_name].filter(Boolean).join(" ")}
                </TableCell>
                <TableCell className={`${classes.cell} ${classes.truncate}`} title={r.email || ""}>
                  {r.email}
                </TableCell>
                <TableCell className={classes.cell}>{r.phone}</TableCell>
                <TableCell className={`${classes.cell} ${classes.truncate}`}
                  title={[r.brand, r.model].filter(Boolean).join(" ")}>
                  {[r.brand, r.model].filter(Boolean).join(" ").trim()}
                </TableCell>
                <TableCell className={classes.cell}>
                  {r.rma_no || <span className={classes.muted}>—</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {loading && (
          <div className={classes.loading}>
            <CircularProgress size={22} />
          </div>
        )}
        {!loading && !rows.length && !error && (
          <div className={classes.loading}>
            <span className={classes.muted}>No repair requests match.</span>
          </div>
        )}
      </Paper>
    </Box>
  );
}

export default RepairsPage;
